import './TextField.scss';

import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';

import { numericRegex, onlyNumber, onlyPasteNumber } from 'shared/utils/utils';

import { ReactComponent as AlertIcon } from 'assets/icons/alert.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ReactComponent as DropDownIcon } from 'assets/icons/dropdown.svg';
import { ReactComponent as HideIcon } from 'assets/icons/eye-close.svg';
import { ReactComponent as ShowIcon } from 'assets/icons/eye.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import cx from 'utils/cx';
import IconButton from './IconButton';

const TextField = ({
  className,
  label,
  name,
  required,
  rules,
  error,
  defaultValue,
  shouldUnregister,
  prefix,
  suffix,
  disabled,
  textarea,
  autosize,
  children,
  number,
  type,
  readOnly,
  onClick,
  onChange,
  suffixType,
  ...rest
}) => {
  const { t } = useTranslation('common');
  // eslint-disable-next-line no-nested-ternary
  const InputElement = autosize
    ? TextareaAutosize
    : textarea
    ? 'textarea'
    : 'input';
  const extraRules = Number.isInteger(number)
    ? { minLength: { value: number, message: number + t('digitsRequired') } }
    : {};

  const {
    field,
    fieldState: { error: fieldError },
  } = useController({
    name,
    rules: { required, ...extraRules, ...rules },
    defaultValue,
    shouldUnregister,
  });
  const errorGlobal = error || fieldError;

  const [isFocus, setFocus] = useState(false);
  const onFocus = () => setFocus(true);
  const onBlur = () => {
    setFocus(false);
    field.onBlur();
  };

  const [isActive, setActive] = useState(false);
  const onMouseEnter = () => setActive(true);
  const onMouseLeave = () => setActive(false);

  const [inputType, setInputType] = useState(type);

  const onChangeToNumber = (e) => {
    const { value } = e.target;
    if (value.match(numericRegex)) {
      field.onChange(+value.replace(numericRegex, ''));
    } else {
      field.onChange(value);
    }
  };

  const onChangeText = (e) => {
    if (number) {
      onChangeToNumber(e);
    } else {
      field.onChange(e);
    }
    onChange(e);
  };

  const getPrefixIcon = () => {
    if (prefix === 'search') return <SearchIcon />;
    if (prefix === 'calendar') return <CalendarIcon />;
    return null;
  };

  const getSuffix = () => {
    if (suffix === 'search') {
      return (
        <IconButton
          className={cx('suffix', suffix)}
          type={suffixType}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
        >
          <SearchIcon />
        </IconButton>
      );
    }
    if (suffix === 'dropdown') {
      return (
        <IconButton className={cx('suffix', suffix)} onClick={onClick}>
          <DropDownIcon />
        </IconButton>
      );
    }
    if (suffix === 'password') {
      return (
        <IconButton
          className={cx('suffix', suffix)}
          onClick={() => {
            inputType === 'password'
              ? setInputType('text')
              : setInputType('password');
          }}
        >
          {inputType === 'password' ? <ShowIcon /> : <HideIcon />}
        </IconButton>
      );
    }
    return null;
  };

  const getErrorPrompt = () => {
    if (!!errorGlobal && typeof error !== 'boolean') {
      return (
        <div className="form-message-wrapper">
          <AlertIcon />
          <span>
            {errorGlobal.type === 'required'
              ? errorGlobal.message || t('isRequired')
              : errorGlobal.message}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={cx('form-group', className)}>
      {label && <span className={cx('label', { required })}>{label}</span>}
      {children}
      <div
        className={cx(
          'form-wrapper',
          {
            error: errorGlobal,
            disabled,
            textarea,
            isfocus: isFocus,
            active: isActive,
            autosize,
            readonly: readOnly,
          },
          suffix && `suffix-${suffix}`,
        )}
      >
        {prefix && <div className="prefix">{getPrefixIcon()}</div>}
        <InputElement
          ref={field.ref}
          className="input"
          value={field.value || ''}
          disabled={disabled}
          type={inputType}
          onFocus={onFocus}
          onBlur={onBlur}
          maxLength={Number.isInteger(number) ? number : undefined}
          onKeyDown={number ? onlyNumber : undefined}
          onPaste={number ? onlyPasteNumber : undefined}
          onChange={onChangeText}
          {...(autosize ? { minRows: 1, maxRows: 7 } : undefined)}
          readOnly={readOnly}
          onClick={onClick}
          {...rest}
        />
        {getSuffix()}
      </div>
      {getErrorPrompt()}
    </div>
  );
};

TextField.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  error: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.bool,
  ]),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  disabled: PropTypes.bool,
  textarea: PropTypes.bool,
  children: PropTypes.node,
  number: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  type: PropTypes.string,
  autosize: PropTypes.bool,
  readOnly: PropTypes.bool,
  rules: PropTypes.object,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  defaultValue: PropTypes.string,
  shouldUnregister: PropTypes.bool,
  suffixType: PropTypes.string,
};

TextField.defaultProps = {
  className: null,
  label: '',
  name: '',
  required: false,
  error: null,
  prefix: '',
  suffix: '',
  disabled: false,
  textarea: false,
  children: null,
  number: null,
  type: 'text',
  autosize: false,
  readOnly: false,
  rules: {},
  onClick: () => {},
  onChange: () => {},
  defaultValue: '',
  shouldUnregister: false,
  suffixType: 'submit',
};

export default TextField;
